function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

import { get, getCurrentTime } from '../utils/utils';
import { CRC32 } from './crc32';
import { Uploader } from './upload/upload-collection';
import { sendBi } from './bi/send-request';
import { BIEventIds } from './bi/event-ids';
import { toError } from '../sources/private/parsers/error';
import { extractCancelAndProgressConfig, isAborted } from '../utils/http';
import { UploadStatus } from '../constants/upload-status';
import { UploadEvent } from '../constants/upload-event';
import { ContentType } from '../constants/ContentType';
var HASH_SIZE = 1024 * 1024;

function getBlobArrayBuffer(blob) {
  return new Promise(function (resolve, reject) {
    var reader = new FileReader();
    reader.readAsArrayBuffer(blob);

    reader.onload = function (event) {
      var arrayBuffer = new Int32Array(event.target.result);
      resolve(arrayBuffer);
    };

    reader.onerror = function (reason) {
      reject(reason);
    };
  });
}

function getHash(blob) {
  return getBlobArrayBuffer(blob.slice(0, HASH_SIZE)).then(function (arrayBuffer) {
    return CRC32.buf(arrayBuffer).toString(16);
  }, toError);
}

function getSliceFunction(file) {
  var funcToSlice;

  if (file.slice) {
    funcToSlice = 'slice';
  } else if (file.mozSlice) {
    funcToSlice = 'mozSlice';
  } else if (file.webkitSlice) {
    funcToSlice = 'webkitSlice';
  } else {
    funcToSlice = 'slice';
  }

  return funcToSlice;
}

function getBytesToUpload(chunksToUpload) {
  return chunksToUpload.reduce(function (prevValue, nextValue) {
    return prevValue + get(nextValue, 'data.resumableCurrentChunkSize', 0);
  }, 0);
}

function getFileId(file, chunksLength) {
  var startHash = getHash(file.file.slice(0, HASH_SIZE));
  var endHash = getHash(file.file.slice(-HASH_SIZE));
  return Promise.all([startHash, endHash]).then(function (response) {
    return response[0] + "_" + response[1] + "_" + chunksLength;
  }, toError);
}

function getChunkSize(size) {
  // 100MB
  if (size < 104857600) {
    return 10485760; // 10MB
  } // 500MB


  if (size < 524288000) {
    return 26214400; // 25MB
  } // 2GB


  if (size < 2147483648) {
    return 52428800; // 50MB
  } // 5GB


  if (size < 5368709120) {
    return 104857600; // 100MB
  }

  return Math.ceil(size / 100);
}

function getChunksFromFile(fileSource) {
  var file = fileSource.file;
  var funcToSlice = getSliceFunction(file);
  var chunks = [];
  var startBytes = 0;
  var chunkSize = getChunkSize(file.size);

  while (startBytes < file.size) {
    chunks.push(file[funcToSlice](startBytes, startBytes + chunkSize));
    startBytes += chunkSize;
  }

  return chunks;
}

function getNotReadyChunks(chunksStatuses) {
  return chunksStatuses.filter(function (chunk) {
    return chunk.status !== 'READY';
  });
}

export function initResumable(settings, adapter, itemMapper, http) {
  /*
  function checkChunkUploadStatus(fileId, chunkNumber, options) {
    var data = {
      resumableIdentifier: fileId,
      resumableChunkNumber: chunkNumber
    };
     return http
      .get(settings.apiUrl + '/resumable/chunk/status', data, options)
      .catch(function (error) {
        return toError(error);
      });
  }
  */
  function checkAllChunks(fileId, chunks) {
    var url = settings.apiUrl + "/resumable/chunk/status";
    var data = {
      resumableIdentifier: fileId,
      resumableTotalChunks: chunks.length
    };
    var options = {
      withCredentials: true,
      headers: {
        'Content-Type': ContentType.UrlEncoded
      }
    };
    return http.post(url, data, null, options).then(function (response) {
      return response.data.resumableChunks;
    }, toError);
  }

  function uploadChunks(chunksToUpload, allChunksLength, file, fileId, resumableUploadPromise, options, resumableCancelAndProgressConfig) {
    var bytesTotalUploaded = 0;
    var currentChunkBytesLoaded = 0;

    if (allChunksLength !== chunksToUpload.length) {
      bytesTotalUploaded = file.file.size - getBytesToUpload(chunksToUpload);
    }

    var fileToUpload = Uploader.uploadQueue.find(function (el) {
      return el.source.file === file.file;
    });
    var uploadedChunkResponses = [];
    return new Promise(function (resolve, reject) {
      function uploadChunk(chunkId) {
        var chunk = chunksToUpload[chunkId];

        if (!chunk) {
          resolve(uploadedChunkResponses);
          return;
        }

        sendBi('/mg', {
          src: 26,
          evid: BIEventIds.BIEventsIds.resumableChunkUploadStarted,
          chunk_size: get(chunk, 'data.chunk.size', -1),
          file_id: fileId,
          file_size: file.file.size,
          total_chunks: allChunksLength
        });
        var chunkUploadStartedAt = getCurrentTime();
        var cancelAndProgressConfig = extractCancelAndProgressConfig();
        cancelAndProgressConfig.progressEventEmitter.on(UploadEvent.Upload, function (progress) {
          var chunkBytesLoaded = progress.loaded >= progress.total ? progress.total : progress.loaded;
          bytesTotalUploaded += chunkBytesLoaded - currentChunkBytesLoaded;
          currentChunkBytesLoaded = chunkBytesLoaded;
          var progressStatus = UploadStatus.Uploading;

          if (bytesTotalUploaded >= file.file.size) {
            progressStatus = UploadStatus.Processing;
          }

          resumableCancelAndProgressConfig.progressEventEmitter.emit(UploadEvent.Upload, {
            loaded: bytesTotalUploaded,
            status: progressStatus,
            total: file.file.size
          });
        });

        fileToUpload.cancelAndProgressConfig.abort = function () {
          cancelAndProgressConfig.abort();
        };

        return http.upload(chunk.uploadUrl, chunk.data, _objectSpread({}, options, {
          cancelAndProgressConfig: cancelAndProgressConfig
        })).then(function (response) {
          var chunkUploadTime = (getCurrentTime() - chunkUploadStartedAt) / 1000;
          sendBi('/mg', {
            src: 26,
            evid: BIEventIds.BIEventsIds.resumableChunkUploadFinished,
            chunk_size: get(chunk, 'data.chunk.size', -1),
            file_id: fileId,
            file_size: file.file.size,
            total_chunks: allChunksLength,
            upload_speed: Math.floor(file.file.size / chunkUploadTime),
            upload_time: Math.floor(chunkUploadTime)
          });
          currentChunkBytesLoaded = 0;
          uploadedChunkResponses.push(response.data);
          uploadChunk(chunkId + 1);
        }, function (reason) {
          // TODO: add support to get new uploading url
          console.warn('upload file error', reason);

          if (isAborted(reason)) {
            return Promise.reject(reason);
          }

          resumableCancelAndProgressConfig.progressEventEmitter.emit(UploadEvent.Upload, {
            loaded: bytesTotalUploaded,
            status: UploadStatus.Failed,
            total: file.file.size
          });
          return Promise.reject(reason);
          /*
          if (get(reason, 'data.message') === 'link expired') {
            console.debug('chunk', chunkId, 'upload link has expired');
             var uploadApiUrl = settings.apiUrl + '/resumable/chunk/url';
            var data = {
              count: 1
            };
             http
              .post(uploadApiUrl, data, null, options)
              .then(
                function (response) {
                  var uploadUrl = get(response, 'data.upload_urls.0');
                   http
                    .upload(uploadUrl, chunk.data, options)
                    .then(
                      function (response) {
                        uploadedChunkResponses.push(response.data);
                         console.debug('chunk', chunkId, 'was uploaded with new link');
                        uploadChunk(chunkId + 1);
                      }
                    );
                }
              );
          }
          */
        })["catch"](function (reason) {
          console.warn('error uploading file', reason);
          reject(reason);
        });
      }

      uploadChunk(0);
    });
  }

  function finalizeItem(fileId, fileSource) {
    var params = {
      resumableIdentifier: fileId,
      content_type: fileSource.file.type,
      file_name: fileSource.file.name,
      media_type: fileSource.media_type,
      parent_folder_id: fileSource.parent_folder_id
    };
    return http.get(settings.apiUrl + "/resumable/finalize", params);
  }

  return function (fileSource, options, resumableCancelAndProgressConfig) {
    options = options || {};
    var fileUploadStartedAt = getCurrentTime();
    var fileId;
    var chunks = getChunksFromFile(fileSource);
    var resumableUploadPromise = new Promise(function (resolve, reject) {
      getFileId(fileSource, chunks.length).then(function (responseFileId) {
        fileId = responseFileId;
      }).then(function () {
        return checkAllChunks(fileId, chunks).then(getNotReadyChunks, toError);
      }).then(function (notReadyChunks) {
        sendBi('/mg', {
          src: 26,
          evid: BIEventIds.BIEventsIds.resumableUploadStarted,
          file_id: fileId,
          file_size: fileSource.file.size,
          uploaded_chunks: chunks.length - notReadyChunks.length,
          total_chunks: chunks.length,
          chunks_status_time: Math.floor((getCurrentTime() - fileUploadStartedAt) / 1000)
        });
        var defaultData = {
          resumableIdentifier: fileId,
          resumableTotalChunks: chunks.length,
          parent_folder_id: fileSource.folderId || null,
          media_type: fileSource.media_type
        };
        var chunksToUpload = notReadyChunks.map(function (chunk) {
          var chunkData = _objectSpread({}, defaultData, {
            chunk: chunks[chunk.chunkNumber],
            resumableChunkNumber: chunk.chunkNumber,
            resumableCurrentChunkSize: chunks[chunk.chunkNumber].size
          });

          return {
            uploadUrl: chunk.uploadUrl,
            data: chunkData
          };
        });
        return uploadChunks(chunksToUpload, chunks.length, fileSource, fileId, resumableUploadPromise, options, resumableCancelAndProgressConfig);
      }).then(function (uploadedChunkResponses) {
        var chunksUploadedInCurrentSession = uploadedChunkResponses.length;
        return finalizeItem(fileId, fileSource).then(function (response) {
          var fileSize = fileSource.file.size;
          var uploadTime = (getCurrentTime() - fileUploadStartedAt) / 1000;
          sendBi('/mg', {
            src: 26,
            evid: BIEventIds.BIEventsIds.resumableUploadFinished,
            file_id: fileId,
            file_size: fileSource.file.size,
            uploaded_chunks: chunksUploadedInCurrentSession,
            total_chunks: chunks.length,
            upload_speed: Math.floor(fileSize / uploadTime),
            upload_time: Math.floor(uploadTime)
          });
          return resolve(response);
        }, function (reason) {
          console.warn('finalize error', reason);
          return Promise.reject(reason);
        });
      })["catch"](function (reason) {
        console.warn('resumable', reason);
        reject(reason);
      });
    });
    return resumableUploadPromise;
  };
}